import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Button } from '@/components/ui/Button';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { useAboutContent } from '@/lib/cms';
import { Skeleton } from '@/components/ui/Skeleton';

const DEFAULTS = {
  story: {
    eyebrow: 'About Vonlof',
    headline: 'A studio of builders, not bystanders.',
    body:
      "Vonlof started in 2024 in Accra with a simple frustration: too much of the world's best software is built for markets that aren't ours. We wanted to change that — without compromising on craft, scale, or taste.",
    pull_quote: "We wanted to change that — without compromising on craft, scale, or taste.",
    pull_quote_attr: 'Founding statement',
  },
  mission: {
    eyebrow: 'Our mission',
    headline: 'Solve meaningful problems through technology.',
    body:
      'To solve meaningful problems through technology by creating modern, scalable, and impactful digital products for Africa and the global market.',
  },
  vision: {
    eyebrow: 'Our vision',
    headline: "One of Africa's most innovative technology companies.",
    body: "To become one of Africa's most innovative technology companies — delivering digital solutions that improve lives worldwide.",
  },
  why_we_exist: {
    eyebrow: 'Why we exist',
    headline: "Markets we serve deserve software built for them.",
    body:
      "Because the world's most ambitious teams outside Silicon Valley shouldn't have to settle for products designed for someone else's reality.",
  },
} as const;

export function AboutPage() {
  const { data: story } = useAboutContent('story');
  const { data: mission } = useAboutContent('mission');
  const { data: vision } = useAboutContent('vision');
  const { data: whyWeExist } = useAboutContent('why_we_exist');
  const { data: coreValues } = useAboutContent('core_values');
  const { data: timeline } = useAboutContent('timeline');
  const { data: achievements } = useAboutContent('achievements');
  const { data: roadmap } = useAboutContent('roadmap');

  const storyData = { ...DEFAULTS.story, ...(story ?? {}) };
  const missionData = { ...DEFAULTS.mission, ...(mission ?? {}) };
  const visionData = { ...DEFAULTS.vision, ...(vision ?? {}) };
  const whyData = { ...DEFAULTS.why_we_exist, ...(whyWeExist ?? {}) };

  const values = Array.isArray((coreValues as { items?: unknown[] } | null)?.items)
    ? ((coreValues as { items: Array<{ icon: string; name: string; description: string }> }).items)
    : defaultValues();
  const timelineItems = Array.isArray((timeline as { items?: unknown[] } | null)?.items)
    ? ((timeline as { items: Array<{ year: number | string; title: string; description: string }> }).items)
    : defaultTimeline();
  const achievementItems = Array.isArray((achievements as { items?: unknown[] } | null)?.items)
    ? ((achievements as { items: Array<{ value: string; label: string; sublabel?: string }> }).items)
    : defaultAchievements();
  const roadmapItems = Array.isArray((roadmap as { items?: unknown[] } | null)?.items)
    ? ((roadmap as { items: Array<{ horizon: string; bullets: string[] }> }).items)
    : defaultRoadmap();

  return (
    <>
      <Meta title="About" description={storyData.body} path="/about" />
      <main className="pt-24 sm:pt-28 md:pt-32">
        {/* HERO */}
        <MaskReveal className="container-narrow pb-16" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">{storyData.eyebrow}</p>
          </FadeIn>
          <TextSplit
            text={storyData.headline}
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.45}>
            <p className="mt-8 text-lg text-fg-muted">{storyData.body}</p>
          </FadeIn>
          {storyData.pull_quote && (
            <FadeIn delay={0.55}>
              <blockquote className="mt-12 border-l-2 border-gold-500 pl-6 text-xl italic text-fg-muted">
                "{storyData.pull_quote}"
                {storyData.pull_quote_attr && (
                  <footer className="mt-2 text-sm not-italic text-fg-subtle">— {storyData.pull_quote_attr}</footer>
                )}
              </blockquote>
            </FadeIn>
          )}
        </MaskReveal>

        {/* MISSION + VISION */}
        <section className="section-y-lg border-t border-border bg-bg-subtle/30">
          <div className="container-wide grid gap-12 md:grid-cols-2">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">{missionData.eyebrow}</p>
              <h2 className="mt-3 text-h2 text-fg">{missionData.headline}</h2>
              <p className="mt-6 text-fg-muted">{missionData.body}</p>
            </FadeIn>
            <FadeIn delay={0.05}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">{visionData.eyebrow}</p>
              <h2 className="mt-3 text-h2 text-fg">{visionData.headline}</h2>
              <p className="mt-6 text-fg-muted">{visionData.body}</p>
            </FadeIn>
          </div>
        </section>

        {/* WHY WE EXIST */}
        <section className="section-y-lg">
          <div className="container-narrow">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">{whyData.eyebrow}</p>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="mt-3 text-h2 text-fg">{whyData.headline}</h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="mt-6 text-lg text-fg-muted">{whyData.body}</p>
            </FadeIn>
          </div>
        </section>

        {/* CORE VALUES */}
        <section className="section-y-lg bg-bg-subtle/30">
          <div className="container-wide">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Core values</p>
              <h2 className="mt-3 text-h2 text-fg">What we hold ourselves to.</h2>
            </FadeIn>
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {values.map((v) => (
                <StaggerItem key={v.name}>
                  <div className="h-full rounded-xl border border-border bg-bg-elevated/40 p-6">
                    <h3 className="text-xl font-semibold tracking-tight">{v.name}</h3>
                    <p className="mt-2 text-sm text-fg-muted">{v.description}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="section-y-lg">
          <div className="container-narrow">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Timeline</p>
              <h2 className="mt-3 text-h2 text-fg">From founding to today.</h2>
            </FadeIn>
            <ol className="relative mt-12 border-l border-border pl-8">
              {timelineItems.map((item, idx) => (
                <li key={`${item.year}-${idx}`} className="relative pb-10 last:pb-0">
                  <span className="absolute -left-[35px] top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-gold-500 bg-bg">
                    <span className="h-2 w-2 rounded-full bg-gold-500" />
                  </span>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gold-500">{item.year}</p>
                  <h3 className="mt-1 text-lg font-semibold tracking-tight">{item.title}</h3>
                  <p className="mt-1 text-sm text-fg-muted">{item.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ACHIEVEMENTS */}
        <section className="section-y-lg bg-bg-subtle/30">
          <div className="container-wide">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">By the numbers</p>
              <h2 className="mt-3 text-h2 text-fg">What we've shipped.</h2>
            </FadeIn>
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {achievementItems.map((stat, idx) => (
                <StaggerItem key={`${stat.label}-${idx}`}>
                  <div className="rounded-xl border border-border bg-bg-elevated/40 p-6">
                    <p className="display-serif text-5xl text-gold-500">{stat.value}</p>
                    <p className="mt-2 text-sm font-semibold">{stat.label}</p>
                    {stat.sublabel && <p className="text-xs text-fg-subtle">{stat.sublabel}</p>}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* ROADMAP */}
        <section className="section-y-lg">
          <div className="container-wide">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Future roadmap</p>
              <h2 className="mt-3 text-h2 text-fg">Where we're going.</h2>
            </FadeIn>
            <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
              {roadmapItems.map((card) => (
                <StaggerItem key={card.horizon}>
                  <div className="h-full rounded-xl border border-border bg-bg-elevated/40 p-6">
                    <h3 className="display-serif text-2xl text-gold-500">{card.horizon}</h3>
                    <ul className="mt-4 space-y-2">
                      {card.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-fg-muted">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* CTA */}
        <section className="section-y-lg">
          <div className="container-narrow text-center">
            <FadeIn>
              <h2 className="display-serif text-display-l text-fg">Want to build with us?</h2>
            </FadeIn>
            <FadeIn delay={0.05}>
              <p className="mt-4 text-fg-muted">
                Open roles and a contact form — both linked below.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="mt-6 flex justify-center gap-3">
                <Button asChild size="lg">
                  <Link to="/careers">
                    Open roles <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="secondary">
                  <Link to="/contact">Contact us</Link>
                </Button>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>
    </>
  );
}

function defaultValues() {
  return [
    { icon: 'Compass', name: 'Ownership', description: 'We act like owners. We do what we say, we finish what we start.' },
    { icon: 'Hammer', name: 'Craft', description: 'We sweat the details. We hold our work to the standard our users deserve.' },
    { icon: 'Sun', name: 'Clarity', description: 'We write things down. We disagree directly. We choose the simpler path.' },
    { icon: 'Gauge', name: 'Speed', description: 'We ship small and often. We measure twice, we cut once.' },
    { icon: 'Heart', name: 'Care', description: 'We build for the people who use what we build. We never lose that.' },
  ];
}

function defaultTimeline() {
  return [
    { year: 2024, title: 'Founded in Accra', description: 'Two engineers, one conviction: build world-class software from Africa.' },
    { year: 2024, title: 'First client engagement', description: 'Healthcare platform pilot in Ghana.' },
    { year: 2025, title: 'First SaaS launch', description: 'Shipped our first multi-tenant SaaS product.' },
    { year: 2025, title: 'Cross-sector expansion', description: 'Engagements in finance, education, and government.' },
    { year: 2026, title: 'First offshore client', description: 'European partner — milestone cross-border project.' },
    { year: 2026, title: 'Vonlof goes public', description: 'This site. The brand. The promise.' },
  ];
}

function defaultAchievements() {
  return [
    { value: '20+', label: 'engineers', sublabel: 'across 5 timezones' },
    { value: '12', label: 'countries', sublabel: 'where our work ships' },
    { value: '9M+', label: 'users', sublabel: 'on products we built' },
    { value: '98%', label: 'retention', sublabel: 'across active engagements' },
  ];
}

function defaultRoadmap() {
  return [
    {
      horizon: 'Next 6 months',
      bullets: [
        'Launch the Vonlof Open Source programme',
        'Open the Cape Coast engineering hub',
        'Two new SaaS products in private beta',
      ],
    },
    {
      horizon: '12 months',
      bullets: [
        'Expand to Nairobi and Lagos delivery centres',
        'Reach 50+ engineers',
        'Series A readiness',
      ],
    },
    {
      horizon: '24 months',
      bullets: [
        'Pan-African engineering network',
        'Public sector flagship deployment',
        'First Vonlof-branded product launch',
      ],
    },
  ];
}

// Re-export so the dynamic page above is still tree-shakable.
export { Skeleton };